import cn from "classnames";
import Datetime from "components/shared/datetime";
import { GroupedCarSelect } from "components/shared/form";
import AccessPermitsSelect from "components/shared/planning/accessPermitsSelect";
import Select, {
    customFilterSelectStyles,
    defaultMultipleSelectProps,
    OptionWithFederalSubject,
    ValueContainer,
} from "components/shared/select";
import addDays from "date-fns/addDays";
import { bidPointLoadUnloadStatuses } from "helpers/enums";
import React, { useState } from "react";
import { namesList } from "sources/books/drivers";
import { list as logistsList } from "sources/books/employees";
import { getCarGroups, getResponsibles, getTrailerTypes } from "sources/books/planning";
import { list as getRegionGroups } from "sources/books/regionGroups";
import { list as trailersList } from "sources/books/trailers";
import { carNumbersWithBids as trucksList } from "sources/books/trucks";
import { getRegionsByName } from "sources/map";

const SearchForm = (props) => {
    const [filtersOpened, setFiltersOpened] = useState(false);
    const minFilterDate = new Date();
    const maxFilterDate = addDays(minFilterDate, 13);
    const filtersDisabled = props.disableFilters;
    const filtersVisible = filtersOpened && !filtersDisabled;

    const toggleFilter = () => {
        if (filtersDisabled) {
            return;
        }

        setFiltersOpened(!filtersOpened);
    };

    const selectProps = {
        ...defaultMultipleSelectProps,
        styles: { ...customFilterSelectStyles },
        components: { ValueContainer },
    };

    const truckSelect = () => {
        const carSelectProps = {
            ...selectProps,
            hasGroups: true,
            inputProps: { name: "carId" },
            url: trucksList,
            placeholder: "По машине или колонне",
            onChange: props.setMultipleValue,
            propName: "number",
            value: props.transportColumn || props.carId,
            dataIndex: props.transportColumn ? "transportColumnId" : "id",
            values: {
                transportColumn: props.transportColumn,
                car: props.carId,
            },
        };
        return <GroupedCarSelect {...carSelectProps} />;
    };

    const renderPointSelect = () => {
        const selectProps = {
            onChange: ({ name, value, option }) => {
                if (!value) {
                    props.setParam(name, value);
                } else {
                    props.setParam(name, { id: option.id, type: option.type });
                }
            },
            styles: { ...customFilterSelectStyles },
            value: props.params.location ? props.params.location.id : null,
            closeMenuOnSelect: true,
            placeholder: "По стране, ФО, региону или городу",
            inputProps: { name: "location" },
            isMulti: false,
            url: getRegionsByName,
            propName: "name",
            minimumInputLength: 2,
            components: { Option: OptionWithFederalSubject },
        };
        return <Select {...selectProps} />;
    };

    const expandCname = cn("b-filter__collapse-btn", {
        "b-filter__collapse-btn--top": filtersVisible,
        "b-filter__collapse-btn--bottom": !filtersVisible,
    });
    const headerTextCname = cn("b-filter__search-text", {
        "b-filter__search-text--dark": filtersVisible,
    });

    const onlyLogistCarsCname = cn("b-filter__tag--wrap", {
        "b-filter__tag--wrap-selected": props.params.onlyLogistCars,
    });
    const byPlanDateCname = cn("b-filter__tag--wrap", { "b-filter__tag--wrap-selected": props.params.byPlanDate });
    const withMandatoryCname = cn("b-filter__tag--wrap", {
        "b-filter__tag--wrap-selected": props.params.withMandatory,
    });
    const withCountryCname = cn("b-filter__tag--wrap", { "b-filter__tag--wrap-selected": props.params.withCountry });
    const onlyWithExternalBidsCname = cn("b-filter__tag--wrap", {
        "b-filter__tag--wrap-selected": props.onlyWithExternalBids,
    });
    const getNextPlannedBidCname = cn("b-filter__tag--wrap", {
        "b-filter__tag--wrap-selected": props.params.getNextPlannedBid,
    });
    const showDraftInPlanningCname = cn("b-filter__tag--wrap", {
        "b-filter__tag--wrap-selected": props.params.showDraftInPlanning,
    });
    const onlyWithServiceWorkСname = cn("b-filter__tag--wrap", {
        "b-filter__tag--wrap-selected": props.params.onlyWithServiceWork,
    });
    const hideInactiveCarsCname = cn("b-filter__tag--wrap", {
        "b-filter__tag--wrap-selected": props.params.hideInactiveCars,
    });
    const onlyWithDriverNeedsShiftChangeСname = cn("b-filter__tag--wrap", {
        "b-filter__tag--wrap-selected": props.params.onlyWithDriverNeedsShiftChange,
    });

    const refreshData = (e) => {
        e.stopPropagation();
        if (props.disableRefresh) {
            return;
        }

        props.refreshData();
    };

    const onChangeDateFilter = ({ name, value }) => {
        if (props.loadUnloadDate === value) {
            return;
        }
        props.setValue({ name, value });
    };

    return (
        <div className="b-filter">
            <div className="b-filter__head" onClick={filtersDisabled ? undefined : toggleFilter}>
                <div className="b-filter__head--left">
                    {!filtersVisible && <div className="b-filter__filter-icon" />}
                    <div className={headerTextCname}>Фильтры по планированию</div>
                </div>
                <div className="b-filter__head--right">
                    {!filtersVisible && (
                        <>
                            {props.hasFilters && (
                                <div className="b-filter__btn b-filter__btn--reset" onClick={props.resetFilters}>
                                    Сбросить
                                </div>
                            )}
                            <div className="b-filter__btn b-filter__btn--refresh" onClick={refreshData}>
                                Обновить
                            </div>
                        </>
                    )}
                    {!filtersDisabled && <div className={expandCname} />}
                </div>
            </div>
            {filtersVisible && (
                <>
                    <div className="b-filter__body">
                        <div className="b-filter__body-column">
                            <div className="pb-1">{renderPointSelect()}</div>
                            <div className="pb-1">
                                <Select
                                    {...selectProps}
                                    inputProps={{ name: "carLogistId" }}
                                    url={logistsList}
                                    placeholder="По логисту"
                                    onChange={props.setValue}
                                    propName="fullNameOrEmail"
                                    value={props.carLogistId}
                                />
                            </div>
                            <div className="pb-1">
                                <Select
                                    {...selectProps}
                                    inputProps={{ name: "responsibleId" }}
                                    url={getResponsibles}
                                    placeholder="По ответственному"
                                    onChange={props.setValue}
                                    propName="displayName"
                                    value={props.responsibleId}
                                />
                            </div>
                            <div className="pb-1">
                                <Select
                                    {...selectProps}
                                    onChange={props.setValue}
                                    value={props.bidPointLoadUnloadStatus}
                                    placeholder="По статусу загрузки"
                                    inputProps={{ name: "bidPointLoadUnloadStatus" }}
                                    options={bidPointLoadUnloadStatuses}
                                    unSearchable
                                />
                            </div>
                            <div className="pb-1">
                                <Datetime
                                    libOptions={{
                                        className: "b-filter__input",
                                        wrapperClassName: "w-100",
                                        placeholderText: "По дате прибытия",
                                        isClearable: true,
                                        minDate: minFilterDate,
                                        maxDate: maxFilterDate,
                                    }}
                                    inputProps={{ name: "loadUnloadDate", type: "date" }}
                                    onChange={onChangeDateFilter}
                                    value={props.loadUnloadDate}
                                />
                            </div>
                            <div className="pb-1">
                                <Select
                                    {...selectProps}
                                    inputProps={{ name: "regionGroupId" }}
                                    url={getRegionGroups}
                                    filterPropName="id"
                                    propName="name"
                                    placeholder="По группе регионов"
                                    onChange={({ name, value, option }) => {
                                        const newValue = value?.length
                                            ? {
                                                  value,
                                                  regionIds: option.reduce(
                                                      (acc, curr) => [...acc, ...curr.regionIds],
                                                      [],
                                                  ),
                                              }
                                            : null;

                                        props.setValue({ name, value: newValue });
                                    }}
                                    value={props.regionGroupId?.value}
                                    components={{ ValueContainer }}
                                />
                            </div>
                        </div>
                        <div className="b-filter__body-column">
                            <div className="pb-1">{truckSelect()}</div>
                            <div className="pb-1">
                                <Select
                                    {...selectProps}
                                    inputProps={{ name: "carGroupId" }}
                                    url={getCarGroups}
                                    placeholder="По логистической группе"
                                    onChange={props.setValue}
                                    propName="name"
                                    value={props.carGroupId}
                                />
                            </div>
                            <div className="pb-1">
                                <Select
                                    {...selectProps}
                                    inputProps={{ name: "bidTrailerId" }}
                                    url={trailersList}
                                    placeholder="По прицепу"
                                    onChange={props.setValue}
                                    propName="number"
                                    value={props.bidTrailerId}
                                />
                            </div>
                            <div className="pb-1">
                                <Select
                                    {...selectProps}
                                    inputProps={{ name: "bidTrailerTypeId" }}
                                    url={getTrailerTypes}
                                    placeholder="По типу прицепа"
                                    onChange={props.setValue}
                                    propName="displayName"
                                    value={props.bidTrailerTypeId}
                                />
                            </div>
                            <div className="pb-1">
                                <Select
                                    {...selectProps}
                                    inputProps={{ name: "bidDriverId" }}
                                    url={namesList}
                                    filterPropName="fullName"
                                    propName="fullName"
                                    placeholder="По водителю"
                                    onChange={props.setValue}
                                    value={props.bidDriverId}
                                />
                            </div>
                            <div className="pb-1">
                                <AccessPermitsSelect onChange={props.setValue} value={props.accessPermitIds} />
                            </div>
                        </div>
                        <div className="b-filter__body-column">
                            <div
                                className={onlyLogistCarsCname}
                                onClick={props.toggleParam.bind(this, "onlyLogistCars")}
                            >
                                <span
                                    className={
                                        props.params.onlyLogistCars
                                            ? "b-filter__tag--delete"
                                            : "b-filter__tag--checkbox"
                                    }
                                />
                                <span className="b-filter__tag--text">Только мои машины</span>
                            </div>
                            <div className={byPlanDateCname} onClick={props.toggleParam.bind(this, "byPlanDate")}>
                                <span
                                    className={
                                        props.params.byPlanDate ? "b-filter__tag--delete" : "b-filter__tag--checkbox"
                                    }
                                />
                                <span className="b-filter__tag--text">Отображать по плановому времени</span>
                            </div>
                            <div className={withMandatoryCname} onClick={props.toggleParam.bind(this, "withMandatory")}>
                                <span
                                    className={
                                        props.params.withMandatory ? "b-filter__tag--delete" : "b-filter__tag--checkbox"
                                    }
                                />
                                <span className="b-filter__tag--text">Отобразить обязательные заказы</span>
                            </div>
                            <div className={withCountryCname} onClick={props.toggleParam.bind(this, "withCountry")}>
                                <span
                                    className={
                                        props.params.withCountry ? "b-filter__tag--delete" : "b-filter__tag--checkbox"
                                    }
                                />
                                <span className="b-filter__tag--text">Отобразить страны</span>
                            </div>
                            <div
                                className={onlyWithServiceWorkСname}
                                onClick={props.toggleParam.bind(this, "onlyWithServiceWork")}
                            >
                                <span
                                    className={
                                        props.params.onlyWithServiceWork
                                            ? "b-filter__tag--delete"
                                            : "b-filter__tag--checkbox"
                                    }
                                />
                                <span className="b-filter__tag--text">Сервисные работы</span>
                            </div>
                            <div
                                className={hideInactiveCarsCname}
                                onClick={props.toggleParam.bind(this, "hideInactiveCars")}
                            >
                                <span
                                    className={
                                        props.params.hideInactiveCars
                                            ? "b-filter__tag--delete"
                                            : "b-filter__tag--checkbox"
                                    }
                                />
                                <span className="b-filter__tag--text">Скрыть неактивные машины</span>
                            </div>
                            <div
                                className={onlyWithDriverNeedsShiftChangeСname}
                                onClick={props.toggleParam.bind(this, "onlyWithDriverNeedsShiftChange")}
                            >
                                <span
                                    className={
                                        props.params.onlyWithDriverNeedsShiftChange
                                            ? "b-filter__tag--delete"
                                            : "b-filter__tag--checkbox"
                                    }
                                />
                                <span className="b-filter__tag--text">Пересменка</span>
                            </div>
                            {props.forwardingEnabled && (
                                <div
                                    className={onlyWithExternalBidsCname}
                                    onClick={props.setValue.bind(this, {
                                        name: "onlyWithExternalBids",
                                        value: !props.onlyWithExternalBids,
                                    })}
                                >
                                    <span
                                        className={
                                            props.params.onlyWithExternalBids
                                                ? "b-filter__tag--delete"
                                                : "b-filter__tag--checkbox"
                                        }
                                    />
                                    <span className="b-filter__tag--text">Только с внешними заказами</span>
                                </div>
                            )}
                            <div
                                className={getNextPlannedBidCname}
                                onClick={props.toggleParam.bind(this, "getNextPlannedBid")}
                            >
                                <span
                                    className={
                                        props.params.getNextPlannedBid
                                            ? "b-filter__tag--delete"
                                            : "b-filter__tag--checkbox"
                                    }
                                />
                                <span className="b-filter__tag--text">Запланированные выгрузки</span>
                            </div>
                            <div
                                className={showDraftInPlanningCname}
                                onClick={props.toggleParam.bind(this, "showDraftInPlanning")}
                            >
                                <span
                                    className={
                                        props.params.showDraftInPlanning
                                            ? "b-filter__tag--delete"
                                            : "b-filter__tag--checkbox"
                                    }
                                />
                                <span className="b-filter__tag--text">Отображать черновик заявки</span>
                            </div>
                        </div>
                    </div>
                    <div className="b-filter__footer pt-2">
                        {props.hasFilters && (
                            <div className="b-filter__btn b-filter__btn--reset" onClick={props.resetFilters}>
                                Сбросить
                            </div>
                        )}
                        <div className="b-filter__btn b-filter__btn--refresh" onClick={refreshData}>
                            Обновить
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SearchForm;
